import { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "i18n-fe";
import { PageLayout, ConfirmButton, StyledBreadcrumb } from "common-fe";
import { useOrganizations } from "../hooks/useOrganizations";
import { useActiveOrgRole } from "../hooks/useActiveOrgRole";

/**
 * Org settings (gear target from OrgAdmin) — rename + deactivate. Owner-only;
 * the API also enforces owner on updateOrganization. "Delete" is implemented as
 * a reversible deactivate (active:false) rather than a destructive hard delete.
 */
export default function OrgSettings() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { activeOrgId, activeOrg, isOwner, isSuper } = useActiveOrgRole();
  const { updateOrganization } = useOrganizations();

  const [name, setName] = useState(activeOrg?.name || "");
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const breadcrumbs = [
    <StyledBreadcrumb key="org" label={t("orgAdmin.breadcrumb")} onClick={() => navigate("/orgs")} />,
    <StyledBreadcrumb key="settings" label={t("orgSettings.title")} />,
  ];

  if (!activeOrgId) {
    return (
      <PageLayout maxWidth={700} breadcrumbs={breadcrumbs}>
        <Alert severity="info">{t("orgAdmin.noActiveOrg")}</Alert>
      </PageLayout>
    );
  }
  if (!isOwner) {
    return (
      <PageLayout maxWidth={700} breadcrumbs={breadcrumbs}>
        <Alert severity="error">{t("orgSettings.ownerOnly")}</Alert>
      </PageLayout>
    );
  }

  const onRename = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSaving(true);
    try {
      await updateOrganization(activeOrgId, { name: name.trim() });
      setSuccess(t("orgSettings.renameSuccess"));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const onDeactivate = async () => {
    setError(null);
    setDeactivating(true);
    try {
      await updateOrganization(activeOrgId, { active: false });
      navigate("/");
    } catch (err) {
      setError(err.message);
      setDeactivating(false);
    }
  };

  return (
    <PageLayout maxWidth={700} breadcrumbs={breadcrumbs}>
      <Typography variant="h5" sx={{ color: "text.primary", mb: 2 }}>
        {t("orgSettings.title")}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Rename */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }} component="form" onSubmit={onRename}>
        <Typography variant="subtitle1" sx={{ color: "text.primary", mb: 1 }}>
          {t("orgSettings.renameHeading")}
        </Typography>
        <TextField
          fullWidth
          label={t("orgSettings.nameLabel")}
          value={name}
          data-testid="org-name-input"
          onChange={(e) => setName(e.target.value)}
        />
        <Box sx={{ mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !name.trim() || name.trim() === activeOrg?.name}
            data-testid="org-rename-save"
          >
            {saving ? t("orgSettings.saving") : t("orgSettings.save")}
          </Button>
        </Box>
      </Paper>

      {/* Danger zone — deactivate is a super-admin-only action (a footgun for an
          org owner), so it's hidden for everyone else; the API also enforces it. */}
      {isSuper && (
        <Paper elevation={1} sx={{ p: 3, borderColor: "error.main", borderWidth: 1, borderStyle: "solid" }}>
          <Typography variant="subtitle1" sx={{ color: "error.main", mb: 1 }}>
            {t("orgSettings.dangerHeading")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            {t("orgSettings.deactivateDescription")}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <ConfirmButton
            onConfirm={onDeactivate}
            disabled={deactivating}
            buttonText={t("orgSettings.deactivate")}
            confirmText={t("orgSettings.deactivateConfirm")}
            cancelText={t("orgSettings.cancel")}
            color="error"
            data-testid="org-deactivate"
            confirmButtonTestId="org-deactivate-confirm"
            cancelButtonTestId="org-deactivate-cancel"
          />
        </Paper>
      )}
    </PageLayout>
  );
}
